import React, { useState } from 'react';
import {
  Box, Group, Loader, Alert, Text, Title, Table, ScrollArea,
  Paper, SegmentedControl, Badge, Tooltip, Button
} from '@mantine/core';
import { IconAlertCircle, IconAdjustments, IconBook } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import DraftBadge from '../../common/DraftBadge';
import config from '../../../config';

const COMP_YEAR = 2026;

const QUARTER_DATES = {
  Q1: { from: `${COMP_YEAR}-01-01`, to: `${COMP_YEAR}-04-01` },
  Q2: { from: `${COMP_YEAR}-04-01`, to: `${COMP_YEAR}-07-01` },
  Q3: { from: `${COMP_YEAR}-07-01`, to: `${COMP_YEAR}-10-01` },
  Q4: { from: `${COMP_YEAR}-10-01`, to: `${COMP_YEAR + 1}-01-01` },
  FY: { from: `${COMP_YEAR}-01-01`, to: `${COMP_YEAR + 1}-01-01` },
};

function pctColor(pct) {
  if (pct === null || pct === undefined) return 'dimmed';
  if (pct < 0.50) return 'red';
  if (pct < 0.85) return 'orange';
  if (pct >= 1.00) return 'green';
  return 'yellow';
}

function varColor(v) {
  if (v === null || v === undefined) return 'dimmed';
  if (v < -0.05) return 'red';
  if (v >= 0.02) return 'green';
  return 'dimmed';
}

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Math.round(n).toLocaleString();
}

function fmtPct(n) {
  if (n == null) return '—';
  return (n * 100).toFixed(1) + '%';
}

function fmtVar(n, hasData) {
  if (!hasData) return <Text c="dimmed" size="sm">—</Text>;
  return <Text c={varColor(n)} size="sm">{(n * 100).toFixed(1)}%</Text>;
}

export default function TeamCommission() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('Q2');

  const apiUrl = `${config.apiBaseUrl}/sales/commission/team-summary?compYear=${COMP_YEAR}`;
  const { data, isLoading, error } = useAuthenticatedData(apiUrl);

  const reps = data?.reps ?? [];

  function getQuarter(rep) {
    if (period === 'FY') return rep.fullYear;
    const q = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 }[period];
    return rep.quarters?.find(x => x.quarter === q) ?? null;
  }

  const { from, to } = QUARTER_DATES[period];

  return (
    <Box p="xl">
      <DraftBadge />
      <Group justify="space-between" mb="md">
        <Title order={2}>Sales Commissions {COMP_YEAR}</Title>
        <Group gap="xs">
          <Button
            component="a"
            href="/sales/commissions/documentation"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            leftSection={<IconBook size={16} />}
          >
            How commissions work
          </Button>
          <SegmentedControl
            value={period}
            onChange={setPeriod}
            data={['Q1', 'Q2', 'Q3', 'Q4', 'FY']}
          />
        </Group>
      </Group>

      {isLoading && <Loader />}
      {error && (
        <Alert icon={<IconAlertCircle />} color="red" mb="md">
          Failed to load commission data.
        </Alert>
      )}

      {!isLoading && !error && (
        <Paper withBorder>
          <ScrollArea>
            <Table highlightOnHover style={{ minWidth: 900 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Salesperson</Table.Th>
                  <Table.Th>Plan</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Target</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>% Plan</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Variance</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Earned</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>
                    {period === 'FY' ? 'EOY Payout' : 'Paid'}
                  </Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reps.map(rep => {
                  const q = getQuarter(rep);
                  return (
                    <Table.Tr
                      key={rep.repCode}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/sales/manager/commissions/${rep.repCode}`)}
                    >
                      <Table.Td>
                        <Text fw={500}>{rep.displayName || rep.repCode}</Text>
                        <Text size="xs" c="dimmed">{rep.repCode}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">{rep.planName}</Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {fmt$(q?.revenue)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text c="dimmed" size="sm">{fmt$(q?.salesTarget)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Tooltip
                          label={`Baseline: ${fmt$(q?.baselineBonus)} × ${fmtPct(q?.revenuePayout)} rev payout`}
                          disabled={!q}
                        >
                          <Text c={pctColor(q?.pctOfPlan)} size="sm" fw={500}>
                            {q ? fmtPct(q.pctOfPlan) : '—'}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {fmtVar(q?.variance, q?.hasVarianceData)}
                        {q?.varianceRaw != null && (
                          <Text size="xs" c="dimmed" fs="italic">pre-DOS {(q.varianceRaw * 100).toFixed(1)}%</Text>
                        )}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Tooltip
                          label={`Adj bonus: ${fmt$(q?.adjustedBonus)} × ${fmtPct(q?.variancePayout)} var payout`}
                          disabled={!q}
                        >
                          <Text size="sm">{fmt$(q?.finalEarned)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c={q?.paidThisPeriod < 0 ? 'red' : undefined}>
                          {fmt$(q?.paidThisPeriod)}
                        </Text>
                        {period !== 'FY' && q?.holdback > 0 && (
                          <Text size="xs" c="dimmed">{fmt$(q.holdback)} held</Text>
                        )}
                      </Table.Td>
                      <Table.Td onClick={e => e.stopPropagation()}>
                        <IconAdjustments
                          size={16}
                          style={{ cursor: 'pointer', color: 'var(--mantine-color-blue-6)' }}
                          onClick={() =>
                            navigate(`/sales/manager/invoices?ccode=${rep.repCode}&from=${from}&to=${to}`)
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {reps.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text c="dimmed" ta="center" py="md">No commission data</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
    </Box>
  );
}
